import type { Collection } from '@prisma/client'
import type { SerializeFrom } from '@remix-run/server-runtime'
import { createContext, useCallback, useRef, useState } from 'react'

import type { ProductFull } from '~/models/product.server'

type ReorderType = 'products' | 'collections' | null

export interface ShopReorderContextType {
  open: ReorderType
  setOpen: (open: ReorderType) => void
  products: SerializeFrom<ProductFull>[]
  collections: SerializeFrom<Collection>[]
  updateProducts: (products: SerializeFrom<ProductFull>[]) => void
  updateCollections: (collections: SerializeFrom<Collection>[]) => void
  setInitialProducts: (products: SerializeFrom<ProductFull>[]) => void
  setInitialCollections: (collections: SerializeFrom<Collection>[]) => void
  resetProducts: () => void
  resetCollections: () => void
}

export const ShopReorderContext = createContext<ShopReorderContextType>({
  open: null,
  setOpen: () => {},
  products: [],
  collections: [],
  updateProducts: () => {},
  updateCollections: () => {},
  setInitialProducts: () => {},
  setInitialCollections: () => {},
  resetProducts: () => {},
  resetCollections: () => {},
})

export const ShopReorderProvider = ({
  children,
  initialProducts = [],
  initialCollections = [],
}: {
  children: React.ReactNode
  initialProducts?: SerializeFrom<ProductFull>[]
  initialCollections?: SerializeFrom<Collection>[]
}) => {
  const [open, setOpen] = useState<ReorderType>(null)
  const [products, setProducts] =
    useState<SerializeFrom<ProductFull>[]>(initialProducts)
  const [collections, setCollections] =
    useState<SerializeFrom<Collection>[]>(initialCollections)

  const initialProductsRef =
    useRef<SerializeFrom<ProductFull>[]>(initialProducts)
  const initialCollectionsRef =
    useRef<SerializeFrom<Collection>[]>(initialCollections)

  const updateCollections = useCallback(
    (newCollections: SerializeFrom<Collection>[]) => {
      setCollections(newCollections)
    },
    []
  )

  const updateProducts = useCallback(
    (newProducts: SerializeFrom<ProductFull>[]) => {
      setProducts(newProducts)
    },
    []
  )

  const setInitialProducts = useCallback(
    (newProducts: SerializeFrom<ProductFull>[]) => {
      setProducts(newProducts)
      initialProductsRef.current = newProducts
    },
    []
  )

  const setInitialCollections = useCallback(
    (newCollections: SerializeFrom<Collection>[]) => {
      setCollections(newCollections)
      initialCollectionsRef.current = newCollections
    },
    []
  )

  const resetProducts = useCallback(() => {
    setProducts(initialProductsRef.current)
  }, [])

  const resetCollections = useCallback(() => {
    setCollections(initialCollectionsRef.current)
  }, [])

  return (
    <ShopReorderContext.Provider
      value={{
        open,
        setOpen,
        products,
        collections,
        updateProducts,
        updateCollections,
        setInitialProducts,
        setInitialCollections,
        resetProducts,
        resetCollections,
      }}
    >
      {children}
    </ShopReorderContext.Provider>
  )
}
